import { Fragment, type ReactNode } from "react";
import { format } from "date-fns";
import { Loader2, Play, RefreshCw, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type ConsultationRecording,
  type ConsultationRecordingProcessingState,
  type ConsultationRecordingTranscript,
  type RecordingSegment,
  type RecordingStatus,
  type SegmentStatus,
} from "@/components/settings/consultation-recordings-table.types";
import { cn } from "@/lib/utils";

const formatDateLabel = (date: Date | null) =>
  date ? format(date, "dd MMM yyyy") : "—";
const formatTimeLabel = (date: Date | null) =>
  date ? format(date, "hh:mm a") : "—";

const formatLabel = (value: string) => {
  if (!value) return "Not available";
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatTranscriptText = (value: string | null | undefined) => {
  const trimmed = value?.trim() ?? "";
  return trimmed || "Transcript text is not available yet.";
};

const getStatusTone = (status: RecordingStatus | SegmentStatus) => {
  switch (status) {
    case "Ready":
      return "border-primary/30 bg-primary/10 text-primary";
    case "Processing":
      return "border-border/70 bg-muted text-foreground";
    case "Partial":
      return "border-border/70 bg-accent/40 text-accent-foreground";
    case "Failed":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    default:
      return "border-border/70 bg-muted text-muted-foreground";
  }
};

const InfoTile = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="rounded-md bg-muted/30 px-2.5 py-1.5">
    <p className="z-eyebrow text-muted-foreground">{label}</p>
    <p className="mt-0.5 z-caption truncate text-foreground">{value}</p>
  </div>
);

interface ConsultationRecordingDetailsPanelProps {
  recording: ConsultationRecording;
  onClose: () => void;
  isPlaybackOpen: boolean;
  isPlaybackLoading: boolean;
  playbackUrl: string | null;
  playbackStateLabel: string | null;
  playbackError: string | null;
  transcript: ConsultationRecordingTranscript | null;
  isTranscriptLoading: boolean;
  transcriptError: string | null;
  segments: RecordingSegment[];
  isSegmentsLoading: boolean;
  segmentsError: string | null;
  activeProcessingState: ConsultationRecordingProcessingState | null;
  isReprocessing: boolean;
  shouldShowReprocessAction: boolean;
  onPlayClick: () => void;
  onReprocessClick: () => void;
}

export const ConsultationRecordingDetailsPanel = ({
  recording,
  onClose,
  isPlaybackOpen,
  isPlaybackLoading,
  playbackUrl,
  playbackStateLabel,
  playbackError,
  transcript,
  isTranscriptLoading,
  transcriptError,
  segments,
  isSegmentsLoading,
  segmentsError,
  activeProcessingState,
  isReprocessing,
  shouldShowReprocessAction,
  onPlayClick,
  onReprocessClick,
}: ConsultationRecordingDetailsPanelProps) => {
  const isPlayable =
    recording.status === "Ready" || recording.status === "Partial";

  return (
    <aside className="fixed bottom-0 right-0 top-0 z-50 flex w-80 shrink-0 flex-col overflow-hidden rounded-l-3xl border border-border/60 bg-card shadow-sm sm:w-96 xl:relative xl:top-auto xl:z-auto xl:w-80 xl:rounded-3xl 2xl:w-[420px]">
      <div className="shrink-0 border-b border-border/60 px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-0.5">
            <p className="z-eyebrow truncate text-muted-foreground">
              Recording · {formatDateLabel(recording.start)},{" "}
              {formatTimeLabel(recording.start)}
            </p>
            <h2 className="z-body truncate font-medium text-foreground">
              {recording.patient}
            </h2>
            <p className="z-hint truncate text-muted-foreground">
              {recording.provider}
              {recording.providerType ? ` · ${recording.providerType}` : ""}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 rounded-full"
            onClick={onClose}
            aria-label="Close details"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Badge
            variant="outline"
            className={cn(
              "rounded-full px-2 py-0 z-caption font-medium shadow-none",
              getStatusTone(recording.status)
            )}
          >
            {recording.status}
          </Badge>
          <Badge
            variant="outline"
            className="rounded-full border-border/70 bg-background/80 px-2 py-0 z-caption font-normal text-muted-foreground shadow-none"
          >
            {recording.totalDurationMinutes} min
          </Badge>
        </div>
      </div>

      <Tabs
        defaultValue="recording"
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div className="shrink-0 px-4 pt-3">
          <TabsList className="grid h-9 w-full grid-cols-3 rounded-lg bg-muted/60 p-1">
            <TabsTrigger
              value="recording"
              className="z-caption rounded-md text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Recording
            </TabsTrigger>
            <TabsTrigger
              value="transcript"
              className="z-caption rounded-md text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Transcript
            </TabsTrigger>
            <TabsTrigger
              value="segments"
              className="z-caption rounded-md text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Segments
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="recording"
          className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3"
        >
          <div className="space-y-2.5">
            <div className="space-y-2 rounded-lg border border-border/50 bg-muted/20 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="z-caption font-medium text-foreground">
                  Combined playback
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!isPlayable || isPlaybackLoading}
                  className="h-7 gap-1 border-border/70 bg-background/80 px-2 z-caption shadow-none hover:bg-accent/20"
                  onClick={onPlayClick}
                >
                  {isPlaybackLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  {isPlaybackOpen ? "Reload" : "Play"}
                </Button>
              </div>

              {isPlaybackOpen ? (
                playbackError ? (
                  <p className="z-caption text-destructive">{playbackError}</p>
                ) : playbackUrl ? (
                  <audio
                    key={recording.id}
                    className="h-8 w-full"
                    controls
                    controlsList="nodownload noplaybackrate"
                    disablePictureInPicture
                    preload="metadata"
                    src={playbackUrl}
                  >
                    Your browser does not support audio playback.
                  </audio>
                ) : (
                  <p className="z-hint text-muted-foreground">
                    {playbackStateLabel ?? "Playback is not ready yet."}
                  </p>
                )
              ) : (
                <p className="z-hint text-muted-foreground">
                  {isPlayable
                    ? "Press Play to load the combined audio."
                    : "This recording is not ready for playback yet."}
                </p>
              )}
            </div>

            {shouldShowReprocessAction ? (
              <div className="space-y-2 rounded-lg border border-border/50 bg-muted/20 p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="z-caption font-medium text-foreground">
                    Reprocess
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isReprocessing}
                    className="h-7 gap-1 border-border/70 bg-background/80 px-2 z-caption shadow-none hover:bg-accent/20"
                    onClick={onReprocessClick}
                  >
                    {isReprocessing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    Reprocess
                  </Button>
                </div>

                {activeProcessingState ? (
                  <div className="grid gap-2 grid-cols-2">
                    <InfoTile
                      label="Recording"
                      value={activeProcessingState.status}
                    />
                    <InfoTile
                      label="Stitched"
                      value={formatLabel(
                        activeProcessingState.stitchedAudioStatus
                      )}
                    />
                    <InfoTile
                      label="Transcript"
                      value={formatLabel(
                        activeProcessingState.transcriptStatus
                      )}
                    />
                    <InfoTile
                      label="Retries"
                      value={activeProcessingState.finalizationRetryCount}
                    />
                  </div>
                ) : null}

                {activeProcessingState?.finalizationLastError ||
                activeProcessingState?.lastError ? (
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 z-hint text-destructive">
                    {activeProcessingState.finalizationLastError ||
                      activeProcessingState.lastError}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent
          value="transcript"
          className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3"
        >
          {isTranscriptLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/30 px-2.5 py-2 z-hint text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading transcript...
            </div>
          ) : transcriptError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 z-hint text-destructive">
              {transcriptError}
            </div>
          ) : transcript ? (
            <div className="space-y-2.5">
              <div className="flex flex-wrap items-center gap-1">
                <Badge
                  variant="outline"
                  className="rounded-full border-border/70 bg-background/80 px-2 py-0 z-caption font-normal text-muted-foreground shadow-none"
                >
                  {formatLabel(transcript.status)}
                </Badge>
                {transcript.language ? (
                  <Badge
                    variant="outline"
                    className="rounded-full border-border/70 bg-background/80 px-2 py-0 z-caption font-normal text-muted-foreground shadow-none"
                  >
                    {transcript.language.toUpperCase()}
                  </Badge>
                ) : null}
                {transcript.durationSeconds ? (
                  <Badge
                    variant="outline"
                    className="rounded-full border-border/70 bg-background/80 px-2 py-0 z-caption font-normal text-muted-foreground shadow-none"
                  >
                    {Math.round(transcript.durationSeconds / 60)} min
                  </Badge>
                ) : null}
              </div>

              <div className="rounded-lg border border-border/50 bg-muted/20 p-2.5">
                <p className="z-eyebrow text-muted-foreground">
                  Final Transcript
                </p>
                <p className="mt-1 whitespace-pre-wrap z-hint leading-snug text-foreground">
                  {formatTranscriptText(transcript.finalTranscriptText)}
                </p>
              </div>

              {transcript.lastError ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 z-hint text-destructive">
                  {transcript.lastError}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border/50 px-2.5 py-2 z-hint text-muted-foreground">
              Transcript is not ready yet for this recording.
            </div>
          )}
        </TabsContent>

        <TabsContent
          value="segments"
          className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3"
        >
          {isSegmentsLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/30 px-2.5 py-2 z-hint text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading segments...
            </div>
          ) : segmentsError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 z-hint text-destructive">
              {segmentsError}
            </div>
          ) : segments.length === 0 ? (
            <div className="rounded-md border border-dashed border-border/50 px-2.5 py-2 z-hint text-muted-foreground">
              No segment details are available for this recording yet.
            </div>
          ) : (
            <div className="space-y-1.5">
              {segments.map((segment) => (
                <Fragment key={`${recording.id}-${segment.segmentNumber}`}>
                  {segment.gapBeforeMinutes && segment.gapBeforeMinutes > 0 ? (
                    <div className="flex justify-center py-0.5">
                      <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0 z-caption text-muted-foreground">
                        Gap {segment.gapBeforeMinutes}m
                      </span>
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-2.5 py-1.5">
                    <span className="z-caption font-medium tabular-nums text-muted-foreground">
                      #{segment.segmentNumber}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="z-caption tabular-nums text-foreground">
                        {formatTimeLabel(segment.start)} –{" "}
                        {formatTimeLabel(segment.end)}
                      </p>
                      <p className="z-eyebrow tabular-nums text-muted-foreground">
                        {segment.durationMinutes} min
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0 z-caption font-medium shadow-none",
                        getStatusTone(segment.status)
                      )}
                    >
                      {segment.status}
                    </Badge>
                  </div>
                </Fragment>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </aside>
  );
};

export default ConsultationRecordingDetailsPanel;
